/**
 * ModernTemplate.tsx
 *
 * Propósito: Template "tech-savvy" para agentes inmobiliarios que quieren
 *            comunicar modernidad y dominio digital. Dark mode editorial
 *            con acentos violeta/índigo/cyan. Carisma "Aurora Constellation".
 * Plan mínimo: starter
 * Privado: No
 * Refactorizado: Abril 2026
 *
 * Checklist de 7 puntos:
 *  [x] 1. Interface AgentFull desde _shared/types
 *  [x] 2. Helpers _shared/agentHelpers (sin hardcodes show_*)
 *  [x] 3. Patrón <a> seguro (atributo en línea de apertura)
 *  [x] 4. Footer con documentos legales + cache useRef
 *  [x] 5. Disclaimers NOM en cards de propiedades
 *  [x] 6. Sello NOM-247 vía useNomSeal
 *  [x] 7. SEO + Schema.org via setPageSEO
 */

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Phone, Mail, MapPin, ArrowRight, Home, Building, Building2, Key, Briefcase,
  Bed, Bath, Maximize, Toilet, Zap, Cpu, Wifi, Shield, Star,
  Facebook, Instagram, Linkedin, Youtube, X as CloseIcon,
} from 'lucide-react'
import { motion } from 'framer-motion'

import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, {
  FILTERS_INITIAL_STATE,
  PropertyFiltersState,
  applyFilters,
} from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

import type { AgentTemplateProps, Property, LegalDocType } from './_shared/types'
import {
  getSubdomain,
  formatPrice,
  resolveContactChannels,
  resolveIdentityDisplay,
  resolveImageStrategy,
  resolveLegalDocs,
  resolveChatConfig,
  useNomSeal,
} from './_shared/agentHelpers'
import { setPageSEO } from './_shared/seoHelpers'
import { API_ROUTES } from './_shared/constants'

// ══════════════════════════════════════════════════════════════════════════
// Strings (i18n-ready, mantenidos en inglés tech por carisma)
// ══════════════════════════════════════════════════════════════════════════
const content = {
  hero_badge: 'AI-Powered Real Estate',
  hero_subtitle_fallback: 'Revolutionizing real estate with cutting-edge technology.',
  hero_explore: 'Explore Portfolio',
  properties_badge: 'Smart Properties',
  properties_heading: 'Future Living Spaces',
  properties_empty: 'New listings coming soon.',
  specialties_heading: 'Tech-Driven Specialties',
  zones_heading: 'Connected Zones',
  zones_subtitle: 'Connected Network',
  cta_heading: 'Ready to Experience the Future?',
  cta_chat_fallback: "Let's talk in the chat — I'm here for you.",
  feature_lightning: 'Lightning Fast Results',
  feature_secure: 'Secure Transactions',
  feature_ai: 'AI-Powered Matching',
  bio_heading: 'About the Broker',
  legal_modal_title: 'Legal Document',
  legal_aviso: 'Privacy Notice',
  legal_terminos: 'Terms & Conditions',
  legal_derechos: 'Consumer Rights',
  legal_nom: 'NOM-247 Information',
  disclaimer_price: 'Price subject to change without notice',
  disclaimer_images: 'Images may be illustrative',
  status_preventa: 'Pre-sale',
  status_construccion: 'Under Construction',
  status_terminado: 'Ready',
  footer_rights: 'All rights reserved',
}

const LEGAL_DOC_LABEL: Record<LegalDocType, string> = {
  aviso_privacidad: content.legal_aviso,
  terminos_condiciones: content.legal_terminos,
  derechos_consumidor: content.legal_derechos,
  info_nom247: content.legal_nom,
}

const CONSTRUCTION_LABEL: Record<NonNullable<Property['construction_status']>, string> = {
  preventa: content.status_preventa,
  en_construccion: content.status_construccion,
  terminado: content.status_terminado,
}

// ══════════════════════════════════════════════════════════════════════════
// Helper local: ícono según especialidad
// ══════════════════════════════════════════════════════════════════════════
const getSpecialtyIcon = (specialty: string) => {
  const lower = specialty.toLowerCase()
  if (lower.includes('casa')) return Home
  if (lower.includes('depa') || lower.includes('departamento')) return Building
  if (lower.includes('preventa')) return Zap
  if (lower.includes('renta')) return Key
  if (lower.includes('leasing') || lower.includes('oficina')) return Briefcase
  return Building2
}

// ══════════════════════════════════════════════════════════════════════════
// Componente
// ══════════════════════════════════════════════════════════════════════════
const ModernTemplate: React.FC<AgentTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()

  // ── Helpers resueltos ────────────────────────────────────────────────
  const contact = resolveContactChannels(agent)
  const identity = resolveIdentityDisplay(agent)
  const images = resolveImageStrategy(agent)
  const legal = resolveLegalDocs(agent)
  const chat = resolveChatConfig(agent)
  const nomSeal = useNomSeal(agent)

  // ── Estado ───────────────────────────────────────────────────────────
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  const [legalModal, setLegalModal] = useState<{
    open: boolean
    type: LegalDocType | ''
    html: string
  }>({ open: false, type: '', html: '' })
  const legalDocsCache = useRef<Record<string, string>>({})

  // ── Carga dinámica de Space Grotesk ─────────────────────────────────
  useEffect(() => {
    const id = 'modern-template-fonts'
    if (document.getElementById(id)) return
    const preconnect1 = document.createElement('link')
    preconnect1.rel = 'preconnect'
    preconnect1.href = 'https://fonts.googleapis.com'
    const preconnect2 = document.createElement('link')
    preconnect2.rel = 'preconnect'
    preconnect2.href = 'https://fonts.gstatic.com'
    preconnect2.crossOrigin = 'anonymous'
    const fontLink = document.createElement('link')
    fontLink.id = id
    fontLink.rel = 'stylesheet'
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&display=swap'
    document.head.appendChild(preconnect1)
    document.head.appendChild(preconnect2)
    document.head.appendChild(fontLink)
  }, [])

  // ── SEO ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setPageSEO(agent)
  }, [agent])

  // ── Fetch propiedades ────────────────────────────────────────────────
  useEffect(() => {
    const subdomain = getSubdomain()
    fetch(API_ROUTES.publicProperties(subdomain))
      .then(r => (r.ok ? r.json() : []))
      .then((data: Property[]) => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
  }, [])

  // ── Reset paginación al cambiar filtros ──────────────────────────────
  useEffect(() => { setCurrentPage(1) }, [filters])

  // ── Derivados ────────────────────────────────────────────────────────
  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)

  const handlePropertyClick = (seoTitle: string) => navigate(`/property/${seoTitle}`)

  // ── Documentos legales: fetch con cache en memoria ───────────────────
  const openLegalDoc = async (docType: LegalDocType) => {
    if (legalDocsCache.current[docType]) {
      setLegalModal({ open: true, type: docType, html: legalDocsCache.current[docType] })
      return
    }
    try {
      const subdomain = getSubdomain()
      const res = await fetch(API_ROUTES.legalDocPreview(docType, subdomain))
      if (!res.ok) return
      const data = await res.json()
      const html = (data?.html ?? '') as string
      legalDocsCache.current[docType] = html
      setLegalModal({ open: true, type: docType, html })
    } catch {
      // silenciar — el botón simplemente no abre el modal
    }
  }

  const closeLegalDoc = () => setLegalModal({ open: false, type: '', html: '' })

  // ── Año actual para footer ──────────────────────────────────────────
  const currentYear = new Date().getFullYear()

  // ── Estilo tipográfico para titulares ──────────────────────────────
  const headingFont = { fontFamily: "'Space Grotesk', 'Inter', sans-serif" }

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden"
      style={{ backgroundColor: '#0a0a0f' }}
    >
      {/* ── BACKGROUND: Aurora Constellation ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {/* Mesh estático suave */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 60% at 15% 10%, #7c3aed 0%, transparent 60%), radial-gradient(ellipse 70% 60% at 85% 95%, #4338ca 0%, transparent 60%)',
          }}
        />
        {/* Grano sutil para riqueza visual */}
        <div
          className="absolute inset-0 opacity-[0.025] mix-blend-overlay"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'><filter id=\'n\'><feTurbulence baseFrequency=\'0.9\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>")',
          }}
        />
      </div>

      {/* ── SELLO NOM-247 (top-right absoluto) ── */}
      {nomSeal.visible && (
        <a href={nomSeal.verifyUrl!}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={nomSeal.copy.ariaLabel}
          title={nomSeal.copy.tooltip}
          className="fixed top-4 right-4 md:top-6 md:right-6 z-50 flex items-center gap-2 group"
        >
          <img
            src={nomSeal.assetPath}
            alt={nomSeal.copy.altText}
            className="h-10 sm:h-14 lg:h-16 xl:h-20 w-auto drop-shadow-2xl group-hover:scale-105 transition-transform duration-300"
          />
          <span className="sr-only md:not-sr-only md:text-[10px] md:font-semibold md:tracking-wider md:uppercase md:text-violet-300/90">
            {nomSeal.copy.linkText}
          </span>
        </a>
      )}

      {/* ── HEADER (zona corporativa) ── */}
      <header className="relative z-20">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
          {/* Logo o nombre comercial tipográfico */}
          <div className="flex items-center gap-3">
            {images.corporate.show && (
              <img
                src={images.corporate.url!}
                alt={identity.corporate.show ? identity.corporate.name : 'Logo'}
                className="h-10 sm:h-12 w-auto object-contain"
              />
            )}
            {!images.corporate.show && identity.corporate.show && (
              <span
                className="text-sm sm:text-base font-bold tracking-widest uppercase text-white/90"
                style={headingFont}
              >
                {identity.corporate.name}
              </span>
            )}
            {!images.corporate.show && !identity.corporate.show && identity.personal.show && (
              <span
                className="text-sm sm:text-base font-bold tracking-widest uppercase text-white/90"
                style={headingFont}
              >
                {identity.personal.name}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* HERO + BIO (zona personal) */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className="relative z-10">
        <div className="container mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-7xl mx-auto"
          >
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-center">
              {/* Left: copy */}
              <div className="space-y-8 order-2 lg:order-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="inline-flex items-center px-3 py-1.5 bg-white/[0.04] backdrop-blur-md rounded-full border border-white/[0.08]"
                >
                  <Cpu className="mr-2 text-cyan-400" size={14} />
                  <span className="text-xs font-medium tracking-wider uppercase text-cyan-300/90">
                    {content.hero_badge}
                  </span>
                </motion.div>

                {identity.personal.show && (
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.7 }}
                    className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
                    style={headingFont}
                  >
                    <span className="bg-gradient-to-br from-white via-violet-100 to-violet-300 bg-clip-text text-transparent">
                      {identity.personal.displayName}
                    </span>
                  </motion.h1>
                )}

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.7 }}
                  className="text-lg sm:text-xl text-slate-300/90 leading-relaxed max-w-xl"
                >
                  {agent.bio || content.hero_subtitle_fallback}
                </motion.p>

                {/* CTAs de contacto */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.7 }}
                  className="flex flex-wrap gap-3"
                >
                  {contact.showPhone && (
                    <a href={`tel:${contact.phoneDigits}`}
                      className="group inline-flex items-center gap-2 px-6 py-3 bg-violet-500/10 hover:bg-violet-500/20 backdrop-blur-md border border-violet-400/30 hover:border-violet-400/60 text-violet-100 font-semibold rounded-full transition-all duration-300"
                    >
                      <Phone size={18} className="text-violet-300" />
                      <span>{contact.phoneNumber}</span>
                    </a>
                  )}
                  {contact.showWhatsapp && (
                    <a href={`https://wa.me/${contact.whatsappDigits}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 backdrop-blur-md border border-emerald-400/30 hover:border-emerald-400/60 text-emerald-100 font-semibold rounded-full transition-all duration-300"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] text-emerald-300">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span>WhatsApp</span>
                    </a>
                  )}
                  {contact.showEmail && (
                    <a href={`mailto:${contact.email}`}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-md border border-white/[0.1] hover:border-white/[0.2] text-white/90 font-semibold rounded-full transition-all duration-300"
                    >
                      <Mail size={18} />
                      <span>Email</span>
                    </a>
                  )}
                  <button
                    onClick={() => document.getElementById('propiedades')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold rounded-full shadow-lg shadow-violet-500/20 hover:shadow-violet-500/40 transition-all duration-300"
                  >
                    <Wifi size={18} />
                    <span>{content.hero_explore}</span>
                  </button>
                </motion.div>

                {/* Fallback al chat si no hay canales */}
                {!contact.hasAnyChannel && chat.showIA && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="text-sm text-violet-200/80 italic flex items-center gap-2"
                  >
                    <ArrowRight size={14} className="animate-pulse" />
                    {content.cta_chat_fallback}
                  </motion.p>
                )}

                {/* Address visible */}
                {contact.showAddress && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="flex items-start gap-2 text-sm text-slate-400"
                  >
                    <MapPin size={16} className="mt-0.5 flex-shrink-0 text-violet-400/70" />
                    <span>{contact.address}</span>
                  </motion.div>
                )}

                {/* Social media */}
                {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                    className="flex gap-2 pt-2"
                  >
                    {agent.social_media.facebook && (
                      <a href={agent.social_media.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="p-2.5 bg-white/[0.04] hover:bg-white/[0.1] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-xl transition-all duration-300"
                      >
                        <Facebook className="w-4 h-4 text-white/80" />
                      </a>
                    )}
                    {agent.social_media.instagram && (
                      <a href={agent.social_media.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="p-2.5 bg-white/[0.04] hover:bg-white/[0.1] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-xl transition-all duration-300"
                      >
                        <Instagram className="w-4 h-4 text-white/80" />
                      </a>
                    )}
                    {agent.social_media.linkedin && (
                      <a href={agent.social_media.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="p-2.5 bg-white/[0.04] hover:bg-white/[0.1] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-xl transition-all duration-300"
                      >
                        <Linkedin className="w-4 h-4 text-white/80" />
                      </a>
                    )}
                    {agent.social_media.tiktok && (
                      <a href={agent.social_media.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="TikTok"
                        className="p-2.5 bg-white/[0.04] hover:bg-white/[0.1] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-xl transition-all duration-300"
                      >
                        <svg className="w-4 h-4 text-white/80" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                        </svg>
                      </a>
                    )}
                    {agent.social_media.youtube && (
                      <a href={agent.social_media.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                        className="p-2.5 bg-white/[0.04] hover:bg-white/[0.1] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-xl transition-all duration-300"
                      >
                        <Youtube className="w-4 h-4 text-white/80" />
                      </a>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Right: avatar (zona personal) */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.9 }}
                className="relative flex justify-center order-1 lg:order-2"
              >
                {images.personal.show && (
                  <div className="relative">
                    <div className="absolute -inset-2 bg-gradient-to-br from-violet-500/40 via-indigo-500/30 to-cyan-500/30 rounded-full blur-2xl opacity-60" />
                    <div className="absolute -inset-1 bg-gradient-to-br from-violet-400/30 to-indigo-400/30 rounded-full blur-md" />
                    <img
                      src={images.personal.url!}
                      alt={identity.personal.name || 'Avatar'}
                      className="relative w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover border border-white/20 shadow-2xl"
                    />
                    <motion.div
                      animate={{ y: [-6, 6, -6] }}
                      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-violet-500/40"
                    >
                      <Star className="text-white" size={20} />
                    </motion.div>
                    <motion.div
                      animate={{ y: [6, -6, 6] }}
                      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -bottom-2 -left-2 w-11 h-11 bg-gradient-to-br from-cyan-500 to-violet-500 rounded-xl flex items-center justify-center shadow-xl shadow-cyan-500/30"
                    >
                      <Shield className="text-white" size={18} />
                    </motion.div>
                  </div>
                )}
                {!images.personal.show && (
                  <div className="relative w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-violet-500/20 to-indigo-500/20 backdrop-blur-md border border-white/10 flex items-center justify-center">
                    <span
                      className="text-7xl lg:text-8xl font-bold text-white/30 tracking-tighter"
                      style={headingFont}
                    >
                      {images.personal.initialsFallback || images.corporate.initialsFallback}
                    </span>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* PROPIEDADES */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {properties.length > 0 && (
        <section id="propiedades" className="relative z-10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <div className="inline-flex items-center px-3 py-1.5 bg-white/[0.04] backdrop-blur-md rounded-full border border-white/[0.08] mb-5">
                <Home className="mr-2 text-cyan-400" size={14} />
                <span className="text-xs font-medium tracking-wider uppercase text-cyan-300/90">
                  {content.properties_badge}
                </span>
              </div>
              <h2
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight bg-gradient-to-br from-white via-violet-100 to-violet-300 bg-clip-text text-transparent"
                style={headingFont}
              >
                {content.properties_heading}
              </h2>
            </motion.div>

            <div className="mb-8">
              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="purple"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {paginatedProperties.map((property, index) => {
                const halfBaths = property.half_bathrooms ?? 0
                const areaNum = property.area_m2 != null ? Number(property.area_m2) : null
                const areaText = areaNum != null && !isNaN(areaNum) ? `${areaNum.toFixed(0)}m²` : null

                return (
                  <motion.article
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.06, 0.4), duration: 0.6 }}
                    viewport={{ once: true }}
                    onClick={() => handlePropertyClick(property.seo_title)}
                    className="group cursor-pointer relative bg-white/[0.03] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10"
                  >
                    {property.featured_image && (
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={property.featured_image}
                          alt={property.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/30 to-transparent" />
                        <div className="absolute top-4 right-4 px-3 py-1.5 bg-violet-600/90 backdrop-blur-md text-white text-xs font-bold tracking-wide rounded-full shadow-lg">
                          {formatPrice(property.price, property.currency)}
                        </div>
                        {property.construction_status && (
                          <div className="absolute top-4 left-4 px-2.5 py-1 bg-cyan-500/20 backdrop-blur-md border border-cyan-400/40 text-cyan-100 text-[10px] font-semibold tracking-wider uppercase rounded-full">
                            {CONSTRUCTION_LABEL[property.construction_status]}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-6 space-y-3">
                      <h3
                        className="text-lg font-bold text-white group-hover:text-violet-200 transition-colors duration-300 line-clamp-2"
                        style={headingFont}
                      >
                        {property.title}
                      </h3>
                      {property.description && (
                        <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">
                          {property.description}
                        </p>
                      )}
                      <div className="flex items-center text-xs text-violet-300/80">
                        <MapPin className="mr-1.5" size={14} />
                        <span className="truncate">{property.zone}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-white/[0.06] text-xs text-slate-400">
                        {property.bedrooms != null && (
                          <span className="flex items-center gap-1">
                            <Bed size={14} className="text-violet-400/70" /> {property.bedrooms}
                          </span>
                        )}
                        {property.bathrooms != null && (
                          <span className="flex items-center gap-1">
                            <Bath size={14} className="text-violet-400/70" /> {property.bathrooms}
                          </span>
                        )}
                        {halfBaths > 0 && (
                          <span className="flex items-center gap-1">
                            <Toilet size={14} className="text-violet-400/70" /> {halfBaths}
                          </span>
                        )}
                        {areaText && (
                          <span className="flex items-center gap-1">
                            <Maximize size={14} className="text-violet-400/70" /> {areaText}
                          </span>
                        )}
                      </div>

                      {!property.featured_image && (
                        <div className="pt-3">
                          <div className="inline-block px-4 py-2 bg-violet-600/90 text-white text-sm font-bold rounded-full">
                            {formatPrice(property.price, property.currency)}
                          </div>
                          {property.construction_status && (
                            <span className="ml-2 px-2.5 py-1 bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 text-[10px] font-semibold tracking-wider uppercase rounded-full">
                              {CONSTRUCTION_LABEL[property.construction_status]}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Disclaimers NOM-247 */}
                      <div className="pt-2 space-y-0.5 text-[10px] text-slate-500 leading-tight">
                        <p>{content.disclaimer_price}</p>
                        <p>{content.disclaimer_images}</p>
                      </div>
                    </div>
                  </motion.article>
                )
              })}
            </div>

            <div className="mt-10">
              <PropertyPagination
                currentPage={currentPage}
                totalPages={totalPages}
                perPage={perPage}
                onPageChange={setCurrentPage}
                onPerPageChange={setPerPage}
                colorSchemeKey="purple"
              />
            </div>
          </div>
        </section>
      )}

      {properties.length === 0 && (
        <section className="relative z-10 py-16 md:py-24">
          <div className="container mx-auto px-6 text-center">
            <p className="text-slate-400 italic">{content.properties_empty}</p>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ESPECIALIDADES */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {agent.specialties && agent.specialties.length > 0 && (
        <section className="relative z-10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-br from-white to-violet-200 bg-clip-text text-transparent"
              style={headingFont}
            >
              {content.specialties_heading}
            </motion.h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6 max-w-6xl mx-auto">
              {agent.specialties.map((specialty, index) => {
                const IconComponent = getSpecialtyIcon(specialty)
                return (
                  <motion.div
                    key={`${specialty}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.08, 0.4), duration: 0.6 }}
                    viewport={{ once: true }}
                    className="group bg-white/[0.03] backdrop-blur-md border border-white/[0.08] hover:border-violet-400/40 rounded-2xl p-6 text-center transition-all duration-400 hover:-translate-y-1"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-violet-600/30 to-indigo-600/30 border border-violet-400/30 rounded-xl flex items-center justify-center group-hover:border-violet-400/60 transition-colors">
                      <IconComponent className="text-violet-200" size={24} />
                    </div>
                    <h3 className="text-sm font-semibold text-white/90 capitalize tracking-wide">
                      {specialty}
                    </h3>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ZONAS */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {agent.zones && agent.zones.length > 0 && (
        <section className="relative z-10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-br from-white to-cyan-200 bg-clip-text text-transparent"
              style={headingFont}
            >
              {content.zones_heading}
            </motion.h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 max-w-6xl mx-auto">
              {agent.zones.map((zone, index) => (
                <motion.div
                  key={`${zone}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(index * 0.06, 0.3), duration: 0.5 }}
                  viewport={{ once: true }}
                  className="group flex items-center gap-4 bg-white/[0.03] backdrop-blur-md border border-white/[0.08] hover:border-cyan-400/40 rounded-2xl p-5 transition-all duration-400 hover:-translate-y-0.5"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-400/30 rounded-xl flex items-center justify-center">
                    <MapPin className="text-cyan-200" size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-white truncate">{zone}</h3>
                    <p className="text-xs text-slate-400">{content.zones_subtitle}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* CTA */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-20 md:py-28">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex w-16 h-16 mb-6 bg-gradient-to-br from-violet-500/30 to-indigo-500/30 backdrop-blur-md border border-violet-400/30 rounded-2xl items-center justify-center">
              <Cpu className="text-violet-200" size={28} />
            </div>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-br from-white via-violet-100 to-violet-300 bg-clip-text text-transparent"
              style={headingFont}
            >
              {content.cta_heading}
            </h2>
            {properties.length > 0 && agent.zones && agent.zones.length > 0 && (
              <p className="text-lg text-slate-300/80 mb-10 max-w-2xl mx-auto">
                {properties.length} smart properties across {agent.zones.length} connected zones.
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-cyan-300/90">
                <Zap size={18} />
                <span className="font-semibold tracking-wide">{content.feature_lightning}</span>
              </div>
              <div className="flex items-center gap-2 text-violet-300/90">
                <Shield size={18} />
                <span className="font-semibold tracking-wide">{content.feature_secure}</span>
              </div>
              <div className="flex items-center gap-2 text-indigo-300/90">
                <Cpu size={18} />
                <span className="font-semibold tracking-wide">{content.feature_ai}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* FOOTER (zona corporativa) */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <footer className="relative z-10 border-t border-white/[0.06] py-10 mt-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Identidad corporativa o personal como fallback */}
            <div className="flex items-center gap-3">
              {images.corporate.show && (
                <img
                  src={images.corporate.url!}
                  alt={identity.corporate.show ? identity.corporate.name : 'Logo'}
                  className="h-8 w-auto object-contain opacity-80"
                />
              )}
              <span className="text-xs text-slate-400">
                © {currentYear}{' '}
                {identity.corporate.show
                  ? identity.corporate.name
                  : identity.personal.show
                    ? identity.personal.name
                    : 'InnovaAgent'}
                . {content.footer_rights}.
              </span>
            </div>

            {/* Documentos legales */}
            {legal.hasAnyDoc && (
              <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
                {legal.availableDocs.map((docType) => (
                  <button
                    key={docType}
                    onClick={() => openLegalDoc(docType)}
                    className="text-slate-400 hover:text-violet-300 transition-colors duration-300"
                  >
                    {LEGAL_DOC_LABEL[docType]}
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Datos legales adicionales */}
          {(contact.showAttentionHours || contact.showComplaintsContact) && (
            <div className="mt-6 pt-6 border-t border-white/[0.04] text-xs text-slate-500 space-y-1 text-center md:text-left">
              {contact.showAttentionHours && (
                <p><span className="text-slate-400">Hours: </span>{contact.attentionHours}</p>
              )}
              {contact.showComplaintsContact && (
                <p><span className="text-slate-400">Complaints: </span>{contact.complaintsContact}</p>
              )}
            </div>
          )}
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* MODAL DE DOCUMENTO LEGAL */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {legalModal.open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={closeLegalDoc}
        >
          <div
            className="bg-[#13131a] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <span className="font-semibold text-sm text-white tracking-wide" style={headingFont}>
                {legalModal.type ? LEGAL_DOC_LABEL[legalModal.type] : content.legal_modal_title}
              </span>
              <button
                onClick={closeLegalDoc}
                aria-label="Close"
                className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors"
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <div
              className="overflow-y-auto p-6 text-sm leading-relaxed text-slate-300 prose prose-invert prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: legalModal.html }}
            />
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* CHAT WIDGET */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {chat.showIA && (
        <ChatWidget
          agentName={agent.name}
          agentSubdomain={getSubdomain()}
          agentAvatar={agent.avatar_url}
          agentPlan={agent.plan}
        />
      )}
    </div>
  )
}

export default ModernTemplate
